<template>
	<div>
		<div class="loader" v-if="loading">
			<div class="background">
				<div id="text">
					<p class="h2">
						{{ this.$t("loading") }}
					</p>
				</div>
			</div>
		</div>
		<div class="row">
			<div class="col-md-2">
				<div class="select-institute d-flex">
					<v-select
						:label="$t('instituteName')"
						v-model="currentIns"
						v-bind:items="institutes"
						item-value="id"
						item-text="name"
					>
					</v-select>
				</div>
			</div>
		</div>
		<div
			class="filters-container mb-2"
			v-if="filters.length && !filtersLoading"
		>
			<h5>{{ $t("filterBy:") }}</h5>
			<div class="row">
				<div class="col-md-1">
					<v-text-field
						:label="$t('tables.id')"
						v-model="tutorSearch.tutor_id"
						clearable
					>
					</v-text-field>
				</div>
				<div class="col-md-2">
					<v-text-field
						:label="$t('tables.name')"
						v-model="tutorSearch.name"
						clearable
					>
					</v-text-field>
				</div>
				<div class="col-md-3">
					<v-text-field
						:label="$t('tables.email')"
						v-model="tutorSearch.email"
						clearable
					>
					</v-text-field>
				</div>
				<div class="col-md-3">
					<vue-tel-input-vuetify
						v-model="tutorSearch.phone_number"
						clearable
						:label="$t('tables.phoneNumber')"
					>
					</vue-tel-input-vuetify>
				</div>
				<div class="col-md-3">
					<v-autocomplete
						v-bind:items="
							filters.find(filter => filter.api_param_name === 'country_ids')
								.options
						"
						item-text="title"
						multiple
						v-model="tutorSearch.country_ids"
						:label="$t('tables.country')"
					>
					</v-autocomplete>
				</div>
			</div>
			<div class="row">
				<div class="col-md-2">
					<v-autocomplete
						v-bind:items="
							filters.find(filter => filter.api_param_name === 'language_ids')
								.options
						"
						item-text="title"
						multiple
						v-model="tutorSearch.language_ids"
						:label="$t('tables.language')"
					>
					</v-autocomplete>
				</div>
				<div class="col-md-2">
					<v-autocomplete
						v-bind:items="genders"
						v-model="tutorSearch.genders"
						multiple
						:label="$t('tables.gender')"
					>
					</v-autocomplete>
				</div>
				<div class="col-md-2">
					<v-autocomplete
						v-bind:items="studentStatuses"
						v-model="tutorSearch.is_active"
						item-value="boolean_value"
						:label="$t('tables.status')"
						clearable
					>
					</v-autocomplete>
				</div>
				<div class="col-md-3">
					<v-dialog
						ref="birthDateDialog"
						v-model="birthDateModal"
						v-bind:return-value.sync="tutorSearch.birth_date"
						presistent
						width="290px"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								v-bind:value="
									tutorSearch.birth_date[0] === '' &&
									tutorSearch.birth_date[1] === ''
										? null
										: tutorSearch.birth_date.join(' ~ ')
								"
								:label="$t('tables.birthDate')"
								prepend-icon=""
								readonly
								v-bind="attrs"
								v-on="on"
							>
							</v-text-field>
						</template>
						<v-date-picker
							v-model="tutorSearch.birth_date"
							range
							show-current
							show-sibling-months
							v-bind:max="todayDate"
							elevation="15"
							active-picker="YEAR"
						>
							<v-spacer></v-spacer>
							<v-btn text color="secondary" v-on:click="birthDateModal = false">
								{{ $t("Cancel") }}
							</v-btn>
							<v-btn
								text
								color="primary"
								v-on:click="$refs.birthDateDialog.save(tutorSearch.birth_date)"
							>
								{{ $t("ok") }}
							</v-btn>
						</v-date-picker>
					</v-dialog>
				</div>
				<div class="col-md-3">
					<v-dialog
						ref="joiningDateDialog"
						v-model="joiningDateModal"
						v-bind:return-value.sync="tutorSearch.joining_date"
						presistent
						width="290px"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								v-bind:value="
									tutorSearch.joining_date[0] === '' &&
									tutorSearch.joining_date[1] === ''
										? null
										: tutorSearch.joining_date.join(' ~ ')
								"
								:label="$t('tables.joiningDate')"
								prepend-icon="mdi-calendar"
								v-bind="attrs"
								v-on="on"
							>
							</v-text-field>
						</template>
						<v-date-picker
							v-model="tutorSearch.joining_date"
							range
							show-current
							show-sibling-months
							v-bind:max="todayDate"
							elevation="15"
							active-picker="YEAR"
						>
							<v-spacer></v-spacer>
							<v-btn
								text
								color="secondary"
								v-on:click="joiningDateModal = false"
							>
								{{ $t("Cancel") }}
							</v-btn>
							<v-btn
								text
								color="primary"
								v-on:click="
									$refs.joiningDateDialog.save(tutorSearch.joining_date)
								"
							>
								Ok
							</v-btn>
						</v-date-picker>
					</v-dialog>
				</div>
			</div>
			<div class="d-flex justify-content-end">
				<v-btn
					text
					color="secondary"
					depressed
					elevation="2"
					v-on:click="clearTutorSearch"
				>
					{{ $t("clear") }}
				</v-btn>
				<v-btn text elevation="2" color="primary" v-on:click="getTutors">
					{{ $t("search") }}
				</v-btn>
			</div>
		</div>
		<div v-if="filtersLoading">
			<div class="spinner-border" role="status"></div>
		</div>
		<v-data-table
			v-bind:items="tutors"
			v-bind:headers="tutorsHeaders"
			item-key="id"
			class="elevation-1"
			v-bind:server-items-length="totalTutorsCount"
			v-bind:options.sync="tutorsOptions"
			v-on:update:options="getTutors"
			v-bind:footer-props="{
				'show-current-page': true,
				'show-first-last-page': true,
				'items-per-page-options': [10, 20, 30, 50, 100, -1],
			}"
			dense
			v-on:click:row="viewTutor"
		>
			<template v-slot:top>
				<v-toolbar flat>
					<v-toolbar-title>{{ $t(tutorsType) }}</v-toolbar-title>
					<v-divider class="mx-4" inset vertical></v-divider>
					<v-spacer></v-spacer>
					<v-btn color="purple" dark depressed v-on:click="printTutors">
						<v-icon left>
							mdi-printer
						</v-icon>
						{{ $t("print") }}
					</v-btn>
					<v-divider class="mx-4" insent vertical></v-divider>
					<v-btn color="primary" @click.prevent="exportMode = true">
						<i class="fa-solid fa-download me-2"></i> {{ $t("exportData") }}
					</v-btn>
				</v-toolbar>
			</template>
			<template v-slot:item.actions="{ item }">
				<v-tooltip top>
					<template v-slot:activator="{ on, attrs }">
						<v-icon
							class="mx-1"
							dense
							v-bind="attrs"
							v-on="on"
							@click.stop="showSessions(item)"
						>
							mdi-calendar-text
						</v-icon>
					</template>
					<span>{{ $t("exportModal.tutorSessions") }}</span>
				</v-tooltip>
				<v-tooltip top>
					<template v-slot:activator="{ on, attrs }">
						<v-icon
							class="mx-1"
							v-bind="attrs"
							v-on="on"
							dense
							v-on:click.stop="completeProfile(item)"
						>
							{{ status === "approved" ? "mdi-pencil" : "mdi-check-decagram" }}
						</v-icon>
					</template>
					<span>{{
						status === "approved"
							? `${$t("exportModal.editProfile")}`
							: `${$t("exportModal.completeProfile")}`
					}}</span>
				</v-tooltip>
				<v-tooltip top v-if="status === 'under_review'">
					<template v-slot:activator="{ on, attrs }">
						<v-icon
							class="mx-1"
							v-bind="attrs"
							v-on="on"
							dense
							v-on:click.stop="approveTutor(item)"
						>
							mdi-account-check
						</v-icon>
					</template>
					<span>{{ $t("exportModal.approve") }}</span>
				</v-tooltip>
				<v-tooltip top v-if="status === 'approved'">
					<template v-slot:activator="{ on, attrs }">
						<v-icon
							class="mx-1"
							v-bind="attrs"
							v-on="on"
							dense
							@click.stop="terminateTutor(item)"
						>
							mdi-archive
						</v-icon>
					</template>
					{{ $t("exportModal.terminate") }}
				</v-tooltip>
			</template>
		</v-data-table>
		<v-dialog
			transition="dialog-bottom-transition"
			max-width="1200"
			v-model="sessionsDialog"
		>
			<v-card>
				<v-card-title>
					<span class="text-h5">
						{{ currentTutor.full_name }} {{ $t("'sSessions") }}
					</span>
					<v-spacer></v-spacer>
					<v-btn
						class="mx-2"
						small
						color="primary"
						@click="sessionsDialog = false"
					>
						<v-icon dark>
							mdi-window-close
						</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text>
					<calendar
						v-bind:events="sessions"
						v-bind:reset="sessionsDialog"
						v-on:update-range="updateRange"
					></calendar>
				</v-card-text>
			</v-card>
		</v-dialog>
		<v-dialog v-model="viewTutorModal" presistent max-width="600">
			<v-card>
				<v-card-title>
					<div class="d-flex w-100 justify-content-between">
						<span class="text-h5 my-auto">{{ $t("tables.details") }}</span>
						<v-btn fab small dark color="primary" v-on:click="closeViewTutor">
							<v-icon dark>
								mdi-close
							</v-icon>
						</v-btn>
					</div>
				</v-card-title>
				<v-card-text>
					<v-container>
						<v-row>
							<v-col cols="12" sm="6" md="4">
								<v-text-field
									:label="this.$t('tables.name')"
									disabled
									v-model="currentTutor.full_name"
								>
								</v-text-field>
							</v-col>
							<v-col cols="12" sm="6" md="4">
								<v-text-field
									:label="this.$t('tables.country')"
									disabled
									v-model="currentTutor.country_name"
								>
								</v-text-field>
							</v-col>
							<v-col cols="12" sm="6" md="4">
								<v-text-field
									:label="this.$t('tables.language')"
									disabled
									v-model="currentTutor.language"
								>
								</v-text-field>
							</v-col>
						</v-row>
						<v-row>
							<v-col cols="12" sm="6" md="4">
								<v-text-field
									:label="this.$t('tables.joiningDate')"
									disabled
									v-model="currentTutor.date_joined"
								>
								</v-text-field>
							</v-col>
							<v-col cols="12" sm="6" md="4">
								<v-text-field
									:label="this.$t('tables.gender')"
									disabled
									v-model="currentTutor.gender"
								>
								</v-text-field>
							</v-col>
							<v-col cols="12" sm="6" md="4">
								<v-text-field
									:label="this.$t('tables.birthDate')"
									disabled
									v-model="currentTutor.date_of_birth"
								>
								</v-text-field>
							</v-col>
						</v-row>
						<v-row>
							<v-col cols="12" sm="6" md="6">
								<v-text-field
									:label="this.$t('tables.email')"
									disabled
									v-model="currentTutor.email"
								>
								</v-text-field>
							</v-col>
							<v-col cols="12" sm="6" md="4">
								<v-text-field
									:label="this.$t('tables.phoneNumber')"
									disabled
									v-model="currentTutor.phone_number"
								>
								</v-text-field>
							</v-col>
							<v-col cols="12" sm="6" md="2">
								<v-text-field
									:label="this.$t('tables.status')"
									disabled
									v-model="currentTutor.active_text"
								>
								</v-text-field>
							</v-col>
						</v-row>
					</v-container>
				</v-card-text>
			</v-card>
		</v-dialog>
		<ExportModal
			:exportMode="exportMode"
			@closeExport="exportMode = false"
			:columns="exportHeaders"
			:parameters="api_parameters"
			:totalListing="totalTutorsCount"
			apiPath="/tutor/operator/listing/"
			method="post"
			api_listing_attribute_name="data.tutors"
			file_response_mode="inline"
		/>
		<TutorEditModal v-bind:tutor="{ ...currentTutor }"></TutorEditModal>
	</div>
</template>

<script>
import GlobalMixin from "@/GlobalMixin.js";
import TutorEditModal from "./TutorEditModal.vue";
import moment from "moment";
import ExportModal from "./ExportData/ExportModal.vue";
import calendar from "../global/calendar.vue";

export default {
	name: "TutorListView",
	mixins: [GlobalMixin],
	components: {
		TutorEditModal,
		ExportModal,
		calendar,
	},
	props: {
		status: {
			type: String,
			required: false,
			default: () => "basic_info",
		},
	},
	data() {
		return {
			tutors: [],
			institutes: [],
			filters: {
				gender: ["Male", "Female"],
				language: ["English", "French"],
				country: ["Egypt", "Saudi Arabia", "Morocco"],
				tutor_status: "under_review",
			},
			currentTutor: {},
			filtersLoading: false,
			currentIns: null,
			loading: false,
			tutorSearch: {
				limit: 20,
				birth_date: ["", ""],
				joining_date: ["", ""],
			},
			birthDateModal: false,
			joiningDateModal: false,

			totalTutorsCount: 0,
			tutorsOptions: {
				page: 1,
				itemsPerPage: 20,
				sortBy: [],
				sortDesc: [],
			},

			// Exporting
			exportMode: false,
			api_parameters: {},
			sessionsDialog: false,
			sessions: [],
			viewTutorModal: false,
			sessionsRange: {},
		};
	},
	mounted() {
		this.loading = true;
		this.axios
			.get(`${process.env.VUE_APP_APIEndPoint}institutes/`, this.headers)
			.then(ins => {
				this.institutes = ins.data.data;
				let params = new URLSearchParams(window.location.search);

				this.axios
					.get(`${process.env.VUE_APP_APIEndPoint}tutor/operator/filters/`)
					.then(res => {
						this.filters = res.data.data.filters;
						// this.currentIns = +JSON.parse(localStorage.vuex)
						//   .auth.user.user.username.split("_")
						//   .at(-1);

						this.currentIns = params.get("ins")
							? parseInt(params.get("ins"))
							: 1;
						this.getTutors();
						this.loading = false;
					});
			});
	},
	methods: {
		closeViewTutor() {
			this.currentTutor = {};
			this.viewTutorModal = false;
		},
		viewTutor(tutor) {
			this.viewTutorModal = true;
			this.currentTutor = tutor;
		},
		clearTutorSearch() {
			this.tutorSearch = {
				limit: 20,
				birth_date: ["", ""],
				joining_date: ["", ""],
			};
			this.getTutors();
		},
		approveTutor(tutor) {
			var self = this;
			Swal.fire({
				title: "Approve?",
				text: `Are you sure you want to approve tutor ${tutor.first_name} ${tutor.last_name} ?`,
				icon: "question",
				confirmButtonText: '<i class="fa fa-thumbs-up"></i> Approve!',
			}).then(result => {
				/* Read more about isConfirmed, isDenied below */
				if (result.isConfirmed) {
					this.loading = true;
					self.axios
						.post(
							`${process.env.VUE_APP_APIEndPoint}tutor/status/change/${tutor.id}/`,
							{ status: "approved" }
						)
						.then(res => {
							window.location.href = "/dashboard/approved-tutors";
						});
				} else if (result.isDenied) {
					Swal.close();
				}
			});
		},
		terminateTutor(tutor) {
			var self = this;
			Swal.fire({
				title: this.$t("terminateModal.terminate"),
				text: `${this.$t("terminateModal.areYouSureYouWantToTerminateTutor")} ${
					tutor.first_name
				} ${tutor.last_name}`,
				icon: "question",
				confirmButtonText: `${this.$t("terminateModal.terminate")} !`,
				cancelButtonText: this.$t("terminateModal.cancel"),
				showCancelButton: true,
			}).then(result => {
				/* Read more about isConfirmed, isDenied below */
				if (result.isConfirmed) {
					self.axios
						.post(
							`${process.env.VUE_APP_APIEndPoint}tutor/status/change/${tutor.id}/`,
							{ status: "terminated" }
						)
						.then(res => {
							window.location.href =
								"/dashboard/terminated-tutors?ins=" + self.currentIns;
						})
						.catch(error => {
							this.formatErrors(error);
							$(".loader").hide();
						});
				} else if (result.isDenied) {
					Swal.close();
				}
			});
		},
		makeSearchString() {
			const queryString = new URL(window.location.href).searchParams;
			const tutorId = queryString.get("tutor-id");
			if (tutorId) {
				return `ins=${this.currentIns}&tutor-id=${tutorId}`;
			} else {
				return `ins=${this.currentIns}`;
			}
		},
		prepareSearchParams() {
			for (let i in this.tutorSearch) {
				if (i !== "is_active" && !this.tutorSearch[i]) {
					delete this.tutorSearch[i];
				}
			}
			if (
				this.tutorSearch.birth_date[0] &&
				this.tutorSearch.birth_date[0] !== ""
			) {
				this.tutorSearch.date_of_birth_from = `${this.tutorSearch.birth_date[0]} 00:00:00`;
			}
			if (
				this.tutorSearch.birth_date[1] &&
				this.tutorSearch.birth_date[1] !== ""
			) {
				this.tutorSearch.date_of_birth_to = `${this.tutorSearch.birth_date[1]} 00:00:00`;
			}
			if (
				this.tutorSearch.joining_date[0] &&
				this.tutorSearch.joining_date[0] !== ""
			) {
				this.tutorSearch.date_joined_from = `${this.tutorSearch.joining_date[0]} 00:00:00`;
			}
			if (
				this.tutorSearch.joining_date[1] &&
				this.tutorSearch.joining_date[1] !== ""
			) {
				this.tutorSearch.date_joined_to = `${this.tutorSearch.joining_date[1]} 00:00:00`;
			}
			if (this.tutorSearch.phone_number) {
				this.tutorSearch.phone_number = this.tutorSearch.phone_number.replaceAll(
					" ",
					""
				);
			}
		},
		getTutors() {
			var self = this;
			this.prepareSearchParams();
			let payload = {
				offset: this.tutorsOptions.itemsPerPage * (this.tutorsOptions.page - 1),
				limit: this.tutorsOptions.itemsPerPage,
				sort_order: this.tutorsOptions.sortDesc[0] ? "desc" : "asc",
				sort_field: this.tutorsOptions.sortBy[0],
				tutor_status: this.status,
				institute: this.currentIns,
				...this.tutorSearch,
			};
			this.api_parameters = payload;
			this.loading = true;
			self.axios
				.post(
					`${process.env.VUE_APP_APIEndPoint}tutor/operator/listing/`,
					payload
				)
				.then(res => {
					self.totalTutorsCount = res.data.data.count;
					self.tutors = res.data.data.tutors.map(tutor => {
						const language = Array.from(new Set(tutor.language));
						return {
							...tutor,
							language,
						};
					});
					const queryString = new URL(window.location.href).searchParams;
					const tutorId = queryString.get("tutor-id");
					if (tutorId) {
						let tutor = this.tutors.find(
							tutor => tutor.id === parseInt(tutorId)
						);
						if (tutor) {
							this.completeProfile(tutor);
						}
					}
					this.loading = false;
					self.filtersLoading = false;
				})
				.catch(error => {
					this.formatErrors(error);
					this.loading = false;
				});
		},
		printTutors() {
			var self = this;
			this.prepareSearchParams();
			let payload = {
				sort_order: this.tutorsOptions.sortDesc[0] ? "desc" : "asc",
				sort_field: this.tutorsOptions.sortBy[0],
				tutor_status: this.status,
				institute: this.currentIns,
				...this.tutorSearch,
				limit: this.totalTutorsCount,
				offset: 0,
			};
			this.loading = true;
			self.axios
				.post(
					`${process.env.VUE_APP_APIEndPoint}institutes/export_pdf/`,
					{
						api_path: "/tutor/operator/listing/",
						api_parameters: payload,
						api_listing_attribute_name: "data.tutors",
						columns_details: [
							{
								name: "ID",
								attribute_path: "id",
							},
							{
								name: "Name",
								attribute_path: "full_name",
							},
							{
								name: "Email",
								attribute_path: "email",
							},
							{
								name: "Phone Number",
								attribute_path: "phone_number",
							},
							{
								name: "Country",
								attribute_path: "country_name",
							},
							{
								name: "Language",
								attribute_path: "language",
							},
							{
								name: "Joining Date",
								attribute_path: "date_joined",
							},
							{
								name: "Gender",
								attribute_path: "gender",
							},
							{
								name: "Birth Date",
								attribute_path: "date_of_birth",
							},
							{
								name: "Status",
								attribute_path: "active_text",
							},
						],
						file_response_mode: "attachment",
					},
					{
						responseType: "blob",
					}
				)
				.then(res => {
					const url = window.URL.createObjectURL(
						new Blob([res.data], { type: "application/pdf" })
					);
					const iframe = document.createElement("iframe");
					iframe.style.display = "none";
					iframe.src = url;
					document.body.appendChild(iframe);
					iframe.onload = () => {
						this.loading = false;
						iframe.contentWindow.focus();
						iframe.contentWindow.print();
					};
				});
		},
		completeProfile(tutor) {
			let tempTutor = tutor;
			this.loading = true;
			this.axios
				.get(
					`${process.env.VUE_APP_APIEndPoint}tutor/bio-info/${tempTutor.id}/`,
					this.headers
				)
				.then(res => {
					tempTutor.bio = res.data.data.tutor_profile.bio_data
						? res.data.data.tutor_profile.bio_data
						: "";
					tempTutor.session_fee = res.data.data.tutor_profile.session_fee
						? res.data.data.tutor_profile.session_fee
						: 0;
					this.axios
						.get(
							`${process.env.VUE_APP_APIEndPoint}tutor/basic-info/${tempTutor.id}/`,
							this.headers
						)
						.then(res2 => {
							tempTutor.avatar = res2.data.data.user.profile_picture_url;
							tempTutor.country_id = res2.data.data.country_id;
							this.axios
								.post(
									`${process.env.VUE_APP_APIEndPoint}tutor/courses/`,
									{
										institute: this.currentIns,
									},
									this.headers
								)
								.then(res3 => {
									tempTutor.courses = {};
									tempTutor.associatedCourses = {};
									tempTutor.courseTypes = {};
									res3.data.data.courses.forEach(course => {
										if (tempTutor.courses[course.course_type_name]) {
											tempTutor.courses[course.course_type_name].push(course);
										} else {
											tempTutor.courses[course.course_type_name] = [course];
											if (tempTutor.courseTypes[course.course_type_id]) {
												tempTutor.courseTypes[course.course_type_id].push(
													course.course_type_name
												);
											} else {
												tempTutor.courseTypes[course.course_type_id] = [
													course.course_type_name,
												];
											}
											tempTutor.associatedCourses[course.course_type_name] = [];
										}
									});
									this.currentTutor = tempTutor;
									this.loading = false;
									var refresh =
										window.location.protocol +
										"//" +
										window.location.host +
										window.location.pathname +
										window.location.search +
										"&tutor-id=" +
										tempTutor.id;
									window.history.pushState({ path: refresh }, "", refresh);
									$("#tutorEditModal").modal({
										backdrop: "static",
										keyboard: false,
									});
									$("#tutorEditModal").modal("show");
								});
						});
				});
		},
		getSessions() {
			this.sessions = [];
			this.loading = true;
			const body = {
				user: this.currentTutor.id,
				institute: this.currentIns,
				include_tutor_available_slots: true,
			};
			if (this.sessionsRange.start) {
				body.from_date = this.sessionsRange.start;
			}
			if (this.sessionsRange.end) {
				body.to_date = this.sessionsRange.end;
			}
			this.axios
				.post(
					`${process.env.VUE_APP_APIEndPoint}reserve/user/sessions/`,
					body,
					this.headers
				)
				.then(res => {
					const sessions = [];
					for (let day in res.data.data.sessions) {
						res.data.data.sessions[day].forEach(session => {
							if (!session.start_date) {
								session.start_date = day;
							}
							if (!session.end_date) {
								session.end_date = day;
							}
							sessions.push(session);
						});
					}
					this.sessions = sessions.map((session, day) => {
						const singleSession = {
							id: session.session_id || session.group_session_id,
							start: new Date(`${session.start_date} ${session.start_time}`),
							end: new Date(`${session.end_date} ${session.end_time}`),
							timed: true,
							start_datetime: moment(
								`${session.start_date} ${session.start_time}`
							),
							end_datetime: moment(`${session.end_date} ${session.end_time}`),
						};

						switch (session.reservation_type) {
							case "custom_program_course":
								singleSession.name = `${session.course.course_type_name}`;
								break;
							case "Group Class":
								singleSession.name = `${session.name}`;
								break;
							case "webinar":
								if (session.webinar) {
									singleSession.name = `${session.webinar.title}`;
								} else if (session.webinar_type) {
									singleSession.name = `${session.webinar_type}`;
								}
								break;
							default:
								singleSession.name = "Unknown";
						}
						const now = new Date().getTime();
						if (
							now < singleSession.end.getTime() &&
							now > singleSession.start.getTime()
						) {
							singleSession.color = "#6A778B";
						} else if (now > singleSession.end.getTime()) {
							switch (session.session_status) {
								case "reserved_missed":
									singleSession.color = "#E64545";
									break;
								case "reserved_attended":
									singleSession.color = "#B4BBC5";
									break;
								case "available":
									singleSession.color = "#90b9f5";
									singleSession.name = "Available";
							}
						} else if (now < singleSession.start.getTime()) {
							switch (session.session_status) {
								case "reserved_pending":
									singleSession.color = "#CC9F47";
									singleSession.name = "Pending";
									break;
								case "available":
									singleSession.color = "#90b9f5";
									singleSession.name = "Available";
									break;
								default:
									singleSession.color = "#5FAE32";
							}
						}
						return singleSession;
					});
					this.loading = false;
				});
		},
		showSessions(tutor) {
			this.currentTutor = tutor;
			this.getSessions();
			this.sessionsDialog = true;
		},
		updateRange({ start, end }) {
			this.sessionsRange = {
				start: start.date,
				end: end.date,
			};
			this.getSessions();
		},
	},
	computed: {
		todayDate() {
			return moment().format("YYYY-MM-DD");
		},
		/**
		 * Get translations for tutor type
		 * @returns string
		 */
		tutorsType() {
			return this.status.replaceAll("_", "") + "TutorsList";
		},
		/**
		 * Headers Data for table
		 * @returns []
		 */
		tutorsHeaders() {
			return [
				{
					value: "id",
					text: this.$t("tables.id"),
				},
				{
					value: "full_name",
					text: this.$t("tables.name"),
				},
				{
					value: "country_name",
					text: this.$t("tables.country"),
				},
				{
					value: "language",
					text: this.$t("tables.language"),
				},
				{
					value: "date_joined",
					text: this.$t("tables.joiningDate"),
				},
				{
					value: "gender",
					text: this.$t("tables.gender"),
				},
				{
					value: "active_text",
					text: this.$t("tables.status"),
				},
				{
					value: "actions",
					text: this.$t("tables.actions"),
					sortable: false,
				},
			];
		},
		exportHeaders() {
			return [
				{
					value: "id",
					text: this.$t("tables.id"),
				},
				{
					value: "full_name",
					text: this.$t("tables.name"),
				},
				{
					value: "email",
					text: this.$t("tables.email"),
				},
				{
					value: "phone_number",
					text: this.$t("tables.phoneNumber"),
				},
				{
					value: "country_name",
					text: this.$t("tables.country"),
				},
				{
					value: "language",
					text: this.$t("tables.language"),
					width: 100,
				},
				{
					value: "date_joined",
					text: this.$t("tables.joiningDate"),
				},
				{
					value: "gender",
					text: this.$t("tables.gender"),
				},
				{
					value: "date_of_birth",
					text: this.$t("tables.birthDate"),
				},
				{
					value: "active_text",
					text: this.$t("tables.status"),
				},
				{
					value: "actions",
					text: this.$t("tables.actions"),
					sortable: false,
				},
			];
		},
	},
	watch: {
		currentIns: {
			handler() {
				if (this.currentIns) {
					this.$router.push(`?${this.makeSearchString()}`);
				}
			},
			immediate: true,
		},
	},
};
</script>

<style scoped lang="scss">
h2,
h5,
p {
	color: #9f9f9f;
}

button {
	padding: 5px;
	margin: 5px;

	&.btn-primary {
		background-color: var(--auth-second-color);
		color: white;
		max-width: none !important;
	}

	&.btn-secondary {
		background-color: transparent;
		border: 1px solid var(--auth-second-color);
		color: var(--auth-second-color) !important;
	}

	&.btn-danger {
		background-color: transparent;
		color: red;
		max-width: none !important;
	}
}

.filters-container {
	color: #9f9f9f;
	font-size: 12px;

	.filter-criteria {
		display: inline-flex;
		margin-right: 15px;
		max-width: fit-content;
		word-break: normal;
		white-space: nowrap;
		flex-wrap: wrap;
		hyphens: auto;

		.option {
			.form-check {
				padding-left: 5px;
				padding-right: 5px;

				.form-check-label {
					margin-bottom: 0;
					font-weight: normal;
					padding-left: 5px;
					color: var(--auth-second-color);
				}

				.form-check-input {
					float: none;

					&:checked {
						background-color: var(--auth-second-color);
						box-shadow: none;
						border-color: var(--auth-second-color);
					}
				}
			}
		}
	}
}
</style>
<style lang="scss">
.dataTables_wrapper .dataTables_paginate {
	margin: auto;
	float: none;
	text-align: center;

	.paginate_button {
		color: #9f9f9f !important;
		border-color: #9f9f9f;
		border-radius: 20px;
		margin: 5px 10px;

		&.current {
			background-color: var(--auth-second-color) !important;
			color: white !important;
		}
	}
}

.loader {
	position: fixed;
	top: 0;
	z-index: 999999;
	left: 0;
	height: 100vh;
	width: 100vw;

	.background {
		background: #2196f39e;
		color: #fff !important;
		display: flex;
		height: 100vh;
		width: 100vw;
		flex-direction: column;
		align-items: center;
		justify-content: center;

		#text {
			display: flex;

			p {
				color: #fff !important;
				margin: auto;
			}
		}
	}

	#coffee,
	#text {
		flex: 1 1 auto;
	}

	#text {
		font-size: 4em;
		font-family: fantasy;
		text-align: center;
	}

	#text p {
		padding: 0;
		margin: 0;
	}

	#text p.h1 {
		font-size: 1.8em;
	}

	#text p.h2 {
		font-size: 1.2em;
	}

	#Layer_1 {
		height: 40vh;
		transform: translateY(25vh);
		opacity: 0;
		-webkit-animation: vew-set 10s ease-in-out forwards;
		animation: vew-set 10s ease-in-out forwards;
	}
}
</style>
